async function resolveThemeModule(mod) {
    const value = mod.default;
    if (typeof value === 'function') {
        const result = value();
        return result instanceof Promise ? await result : result;
    }
    return value;
}
/**
 * ThemeLoader
 * ------------------------
 * The only component in the system that turns a theme *id* into an
 * activated, validated ITheme object. It always goes through the
 * ThemeRegistry (never a direct import), always validates before
 * activation, and always guarantees a safe return value by falling
 * back to the configured default theme on any failure.
 *
 * Core code calls `loadTheme(id)` and receives a ready-to-use ITheme —
 * it never learns whether the requested theme, the default, or neither
 * actually succeeded to load; ThemeEngine surfaces that detail only to
 * whoever explicitly wants it (e.g. a diagnostics panel).
 */
export class ThemeLoader {
    constructor(registry, validator, defaultThemeId) {
        this.registry = registry;
        this.validator = validator;
        this.defaultThemeId = defaultThemeId;
        this.cache = new Map();
    }
    /** Clears the in-memory theme cache (e.g. after a theme package update). */
    clearCache(id) {
        if (id)
            this.cache.delete(id);
        else
            this.cache.clear();
    }
    async loadTheme(id) {
        const requestedThemeIssues = [];
        try {
            const theme = await this.loadAndValidate(id);
            if (theme) {
                return { theme, fellBackToDefault: false, requestedThemeIssues: [] };
            }
        }
        catch (err) {
            requestedThemeIssues.push({
                path: '(load)',
                message: err instanceof Error ? err.message : `Unknown error loading theme "${id}".`,
            });
        }
        // Requested theme failed to load or validate — fall back safely.
        if (id !== this.defaultThemeId) {
            const fallback = await this.loadAndValidate(this.defaultThemeId);
            if (fallback) {
                return { theme: fallback, fellBackToDefault: true, requestedThemeIssues };
            }
        }
        // The default theme itself is missing/invalid — this is a fatal
        // configuration error; the engine has no safe theme to offer.
        throw new Error(`ThemeLoader: unable to load requested theme "${id}" and the default theme ` +
            `"${this.defaultThemeId}" is also unavailable or invalid.`);
    }
    /**
     * Loads (or returns from cache) a theme by id and validates it.
     * Returns null if the theme is not registered or fails validation,
     * rather than throwing, so the caller can decide how to react.
     */
    async loadAndValidate(id) {
        const cached = this.cache.get(id);
        if (cached)
            return cached;
        const entry = this.registry.get(id);
        if (!entry)
            return null;
        const mod = await entry.load();
        const theme = await resolveThemeModule(mod);
        const result = this.validator.validate(theme);
        if (!result.valid)
            return null;
        this.cache.set(id, theme);
        return theme;
    }
}
